import { SetStateAction, useCallback, useState } from 'react'

const setLocalStorageItem = (storage: Storage, key: string, value: unknown) => {
	if (!value) {
		storage.removeItem(key)
		return
	}
	const stringifiedValue = JSON.stringify(value)
	storage.setItem(key, stringifiedValue)
}

const getLocalStorageItem = <T>(storage: Storage, key: string): T | undefined => {
	const value = storage.getItem(key)
	if (!value) return undefined
	try {
		return JSON.parse(value)
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
	} catch (e) {
		return undefined
	}
}

export function useLocalStorage<T = unknown>(
	key: string,
	initialValue?: T,
	storage: Storage = window.localStorage,
) {
	const [value, setValue] = useState(() => getLocalStorageItem<T>(storage, key) ?? initialValue)

	const setState = useCallback(
		(value: SetStateAction<T | undefined>) => {
			setValue(prev => {
				const next =
					typeof value === 'function'
						? (value as (prev: T | undefined) => T | undefined)(prev)
						: value
				setLocalStorageItem(storage, key, next)
				return next
			})
		},
		[key, setValue],
	)

	return [value, setState] as const
}
